import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import api from '../lib/api';
import { toast } from 'sonner';
import { 
  FileText, 
  Loader2, 
  Search, 
  Filter,
  RefreshCw,
  AlertTriangle,
  Info,
  AlertCircle,
  XCircle,
  User,
  Building,
  Clock,
  Activity,
  Shield,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const LEVEL_COLORS = {
  info: 'bg-blue-100 text-blue-700',
  warning: 'bg-yellow-100 text-yellow-700',
  error: 'bg-red-100 text-red-700',
  critical: 'bg-red-200 text-red-800',
};

const LEVEL_ICONS = {
  info: Info,
  warning: AlertTriangle,
  error: AlertCircle,
  critical: XCircle,
};

const CATEGORY_LABELS = {
  'auth.login': 'Connexion',
  'auth.logout': 'Déconnexion',
  'auth.login_failed': 'Échec connexion',
  'user.created': 'Utilisateur créé',
  'user.updated': 'Utilisateur modifié',
  'user.deleted': 'Utilisateur supprimé',
  'user.role_changed': 'Rôle modifié',
  'employee.created': 'Employé créé',
  'employee.updated': 'Employé modifié',
  'employee.deleted': 'Employé supprimé',
  'campaign.created': 'Campagne créée',
  'campaign.activated': 'Campagne activée',
  'campaign.closed': 'Campagne clôturée',
  'evaluation.submitted': 'Évaluation soumise',
  'settings.branding_updated': 'Branding modifié',
  'access.denied': 'Accès refusé',
};

export default function AuditPage() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({
    category: '',
    level: '',
    search: '',
  });
  const [pagination, setPagination] = useState({
    offset: 0,
    limit: 50,
    total: 0,
  });

  useEffect(() => {
    fetchData();
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [pagination.offset, filters.category, filters.level]);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchLogs(),
        fetchStats(),
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const params = new URLSearchParams({
        limit: pagination.limit,
        offset: pagination.offset,
      });
      
      if (filters.category) params.append('category', filters.category);
      if (filters.level) params.append('level', filters.level);
      
      const response = await api.get(`/audit/company?${params}`);
      setLogs(response.data.logs || []);
      setPagination(prev => ({ ...prev, total: response.data.total || 0 }));
    } catch (error) {
      toast.error('Erreur lors du chargement des logs');
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/audit/stats?days=30');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/audit/categories');
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getCategoryLabel = (category) => {
    return CATEGORY_LABELS[category] || category;
  };

  const handleNextPage = () => {
    if (pagination.offset + pagination.limit < pagination.total) {
      setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }));
    }
  };

  const handlePrevPage = () => {
    if (pagination.offset > 0) {
      setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }));
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Audit & Logs">
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-webtech-blue" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Audit & Logs">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Shield className="w-7 h-7 text-webtech-blue" />
              Audit & Logs
            </h1>
            <p className="text-slate-600">
              Journal des actions sensibles de votre entreprise
            </p>
          </div>
          
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total (30 jours)</p>
                  <p className="text-2xl font-bold text-slate-900">{stats?.total || 0}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Activity className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Infos</p>
                  <p className="text-2xl font-bold text-blue-600">{stats?.byLevel?.info || 0}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Info className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Avertissements</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats?.byLevel?.warning || 0}</p>
                </div>
                <div className="p-3 bg-yellow-100 rounded-lg">
                  <AlertTriangle className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Erreurs</p>
                  <p className="text-2xl font-bold text-red-600">{(stats?.byLevel?.error || 0) + (stats?.byLevel?.critical || 0)}</p>
                </div>
                <div className="p-3 bg-red-100 rounded-lg">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input 
                    placeholder="Rechercher..."
                    className="pl-10"
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  />
                </div>
              </div>
              
              <Select 
                value={filters.category} 
                onValueChange={(v) => setFilters(prev => ({ ...prev, category: v === 'all' ? '' : v }))}
              >
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Toutes les catégories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les catégories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {getCategoryLabel(cat.value)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select 
                value={filters.level} 
                onValueChange={(v) => setFilters(prev => ({ ...prev, level: v === 'all' ? '' : v }))}
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Tous les niveaux" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les niveaux</SelectItem>
                  <SelectItem value="info">Info</SelectItem>
                  <SelectItem value="warning">Avertissement</SelectItem>
                  <SelectItem value="error">Erreur</SelectItem>
                  <SelectItem value="critical">Critique</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Logs Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Journal d'audit</CardTitle>
            <CardDescription>
              {pagination.total} entrées au total • Affichage {pagination.offset + 1} - {Math.min(pagination.offset + pagination.limit, pagination.total)}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">Niveau</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Utilisateur</TableHead>
                  <TableHead>Cible</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>IP</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-slate-500 py-12">
                      <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p>Aucun log trouvé</p>
                      <p className="text-sm mt-2">Les actions sensibles seront enregistrées ici</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => {
                    const LevelIcon = LEVEL_ICONS[log.level] || Info;
                    return (
                      <TableRow key={log.id}>
                        <TableCell>
                          <Badge className={LEVEL_COLORS[log.level] || 'bg-gray-100 text-gray-700'}>
                            <LevelIcon className="w-3 h-3 mr-1" />
                            {log.level}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{log.action}</p>
                            <p className="text-xs text-slate-500">{getCategoryLabel(log.category)}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                              <User className="w-4 h-4 text-slate-500" />
                            </div>
                            <div>
                              <p className="text-sm font-medium">{log.userName || '-'}</p>
                              <p className="text-xs text-slate-500">{log.userRole || '-'}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {log.targetType ? (
                            <div>
                              <p className="text-sm">{log.targetName || log.targetId}</p>
                              <p className="text-xs text-slate-500">{log.targetType}</p>
                            </div>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm text-slate-500">
                            <Clock className="w-3 h-3" />
                            {formatDate(log.createdAt)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-slate-500 font-mono">
                            {log.ipAddress || '-'}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>

            {/* Pagination */}
            {logs.length > 0 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-slate-500">
                  Page {Math.floor(pagination.offset / pagination.limit) + 1} sur {Math.ceil(pagination.total / pagination.limit)}
                </p>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handlePrevPage}
                    disabled={pagination.offset === 0}
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Précédent
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleNextPage}
                    disabled={pagination.offset + pagination.limit >= pagination.total}
                  >
                    Suivant
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
